// src/pages/auth/OtpVerify.jsx
import React, { useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Input,
  Alert,
  Label
} from "reactstrap";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";
import { withTranslation } from "react-i18next";
import { useVerifyOtp, useResendOtp } from "../../api/auth";
import CustomHeader from "../../components/Auth/CustomHeader";
import OtpInput from "react-otp-input";

const OTP_LENGTH = 6;

const OtpVerify = (props) => {
  const { t } = props;

  document.title = t("otpVerifyTitle");

  const verifyMutation = useVerifyOtp();
  const resendOtpMutation = useResendOtp();

  // Read query params
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const qpEmail = qp.get("email");
  const qpRole = qp.get("role");
  const qpToken = qp.get("token");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: qpEmail,
      role: qpRole,
      token: qpToken,
      code: ""
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t("otpVerifyInvalidEmail")).required(t("otpVerifyMissingEmail")),
      role: Yup.string().required(t("otpVerifyMissingRole")),
      token: Yup.string().required(t("otpVerifyMissingToken")),
      code: Yup.string()
        .required(t("otpVerifyEnter6Digits"))
        .matches(/^\d+$/, t("otpVerifyDigitsOnly"))
        .length(OTP_LENGTH, t("otpVerifyEnter6Digits"))
    }),
    onSubmit: (values) => {
      const payload = {
        email: values.email,
        role: values.role,
        token: values.token,
        code: values.code
      };

      verifyMutation.mutate(payload, {
        onSuccess: () => {
          toastr.success(t("otpVerifyToastSuccess"));
          if (qpRole === "worker") {
            props.router.navigate(`/worker-submit-agreement?email=${qpEmail}`);
          } else if (qpRole === "client") {
            props.router.navigate(`/client-submit-agreement?email=${qpEmail}`);
          }
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        }
      });
    }
  });

  const onClickResendOtp = (e) => {
    e.preventDefault();
    resendOtpMutation.mutate(
      {
        role: qpRole,
        email: qpEmail,
        fromForgotPassword: false
      },
      {
        onSuccess: () => {
          toastr.success(t("otpVerifyResent"));
          validation.setFieldValue("code", "");
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        }
      }
    );
  };

  const codeError =
    validation.touched.code && validation.errors.code ? validation.errors.code : null;

  return (
    <React.Fragment>
      <CustomHeader />
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardHeader>
                  <h5 className="mb-0">{t("otpVerifyHeader")}</h5>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {verifyMutation.isSuccess && (
                        <Alert color="success" className="mb-3">
                          {t("otpVerifyVerified")}
                        </Alert>
                      )}
                      {verifyMutation.isError && (
                        <Alert color="danger" className="mb-3">
                          {t("otpVerifyVerifyError")}
                        </Alert>
                      )}

                      {/* Context from query params (email / role – person's details) */}
                      <Row className="mb-2">
                        <Col xs={12}>
                          <div className="small text-muted">
                            <div>
                              <strong>{t("otpVerifyEmailLabel")}:</strong>{" "}
                              {validation.values.email || "-"}
                            </div>
                            <div>
                              <strong>{t("otpVerifyRoleLabel")}:</strong>{" "}
                              {validation.values.role || "-"}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <p className="text-muted mb-3">
                        {t("otpVerifyThankYouMessage")}
                      </p>

                      {/* Hidden token (still validated by Yup) */}
                      <Input type="hidden" name="token" value={validation.values.token} readOnly />

                      <div className="mb-2">
                        <Label className="form-label">{t("otpVerifyEnterCodeLabel")}</Label>
                        <div className="d-flex justify-content-center mb-2">
                          <OtpInput
                            value={validation.values.code}
                            onChange={(val) => {
                              const digits = (val || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
                              validation.setFieldValue("code", digits);
                              if (!validation.touched.code) {
                                validation.setFieldTouched("code", true, false);
                              }
                            }}
                            numInputs={OTP_LENGTH}
                            isInputNum
                            shouldAutoFocus
                            inputStyle={{
                              width: "40px",
                              height: "48px",
                              margin: "0 6px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              fontSize: "20px",
                              textAlign: "center"
                            }}
                            renderInput={(props) => <input {...props} />}
                          />
                        </div>
                        {codeError && <div className="invalid-feedback d-block">{codeError}</div>}
                      </div>

                      <div className="text-center mb-3">
                        <p className="text-muted small mb-1">
                          {t("otpVerifySecurityMessage")}
                        </p>
                        <p className="text-muted small mb-0">
                          {t("otpVerifyTeamSignature")}
                        </p>
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary btn-block"
                          disabled={verifyMutation.isPending}
                        >
                          {verifyMutation.isPending
                            ? t("otpVerifyVerifying")
                            : t("otpVerifyVerify")}
                        </button>
                      </div>

                      <div className="mt-3 text-center">
                        <span className="text-muted small">
                          {t("otpVerifyDidntGet")}{" "}
                          <a href="#" onClick={onClickResendOtp}>
                            {t("otpVerifyResend")}
                          </a>
                        </span>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p>
                  <a
                    href="#"
                    className="fw-medium text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      props.router.navigate(-1);
                    }}
                  >
                    {t("otpVerifyBack")}
                  </a>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(withTranslation()(OtpVerify));
